import { CONDITION_FIELD_OPTIONS, OPERATOR_LABELS } from '@/lib/api/sales/automations-api'
import type { SalesFlowCondition, SalesFlowConditionOperator } from '@mediardex/contracts'
import { MdAdd, MdDelete } from 'react-icons/md'

const VALUELESS: SalesFlowConditionOperator[] = ['EXISTS', 'NOT_EXISTS']

/**
 * Lista de condições com E/OU. Campo `conversation.context` usa o operador de
 * contexto por IA (o valor é a descrição do que se espera da conversa).
 */
export default function ConditionsEditor({
    conditions,
    logic,
    onChange,
    emptyText = 'Sem condições: vale para todos.',
}: {
    conditions: SalesFlowCondition[]
    logic: 'AND' | 'OR'
    onChange: (conditions: SalesFlowCondition[], logic: 'AND' | 'OR') => void
    emptyText?: string
}) {
    const update = (index: number, patch: Partial<SalesFlowCondition>) =>
        onChange(conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)), logic)

    return (
        <div className="space-y-3">
            {conditions.length > 1 && (
                <div className="join">
                    {(['AND', 'OR'] as const).map(l => (
                        <button
                            key={l}
                            type="button"
                            className={`btn btn-xs join-item ${logic === l ? 'btn-primary' : 'btn-ghost border-base-300'}`}
                            onClick={() => onChange(conditions, l)}
                        >
                            {l === 'AND' ? 'Todas (E)' : 'Qualquer uma (OU)'}
                        </button>
                    ))}
                </div>
            )}

            {conditions.length === 0 && <p className="text-xs text-base-content/50">{emptyText}</p>}

            {conditions.map((condition, index) => {
                const isContext = condition.field === 'conversation.context' || condition.operator === 'CONTEXT_MATCH'
                return (
                    <div key={index} className="rounded-lg border border-base-300 bg-base-100 p-3 space-y-2">
                        <div className="flex items-center gap-2">
                            <select
                                className="select select-bordered select-sm flex-1 min-w-0"
                                value={condition.field}
                                onChange={e =>
                                    update(index, {
                                        field: e.target.value,
                                        operator: e.target.value === 'conversation.context' ? 'CONTEXT_MATCH' : condition.operator === 'CONTEXT_MATCH' ? 'EQUALS' : condition.operator,
                                    })
                                }
                            >
                                {CONDITION_FIELD_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-square text-error"
                                aria-label="Remover condição"
                                onClick={() => onChange(conditions.filter((_, i) => i !== index), logic)}
                            >
                                <MdDelete />
                            </button>
                        </div>
                        {!isContext && (
                            <select
                                className="select select-bordered select-sm w-full"
                                value={condition.operator}
                                onChange={e => update(index, { operator: e.target.value as SalesFlowConditionOperator })}
                            >
                                {Object.entries(OPERATOR_LABELS)
                                    .filter(([key]) => key !== 'CONTEXT_MATCH')
                                    .map(([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    ))}
                            </select>
                        )}
                        {!VALUELESS.includes(condition.operator) &&
                            (isContext ? (
                                <textarea
                                    className="textarea textarea-bordered textarea-sm w-full leading-relaxed"
                                    rows={2}
                                    placeholder="Ex.: o cliente perguntou sobre preço e não agendou"
                                    value={condition.value}
                                    onChange={e => update(index, { value: e.target.value })}
                                />
                            ) : (
                                <input
                                    type="text"
                                    className="input input-bordered input-sm w-full"
                                    placeholder={condition.operator === 'IN' || condition.operator === 'NOT_IN' ? 'Valores separados por vírgula' : 'Valor'}
                                    value={condition.value}
                                    onChange={e => update(index, { value: e.target.value })}
                                />
                            ))}
                    </div>
                )
            })}

            <button
                type="button"
                className="btn btn-ghost btn-sm gap-1 text-primary"
                onClick={() => onChange([...conditions, { field: 'lead.temperature', operator: 'EQUALS', value: '' }], logic)}
            >
                <MdAdd /> Adicionar condição
            </button>
        </div>
    )
}
